import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea } from 'recharts';
import { TbCopy, TbCheck } from 'react-icons/tb';
import { PiPulseDuotone, PiBracketsCurlyDuotone, PiPlugDuotone, PiArrowsClockwiseDuotone, PiFlagDuotone, PiWarningDuotone, PiCheckCircleDuotone, PiTargetDuotone, PiTimerDuotone } from 'react-icons/pi';
import { FcStart, FcCancel } from 'react-icons/fc';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupToolbar, PopupToolbarGroup, PopupBody, PopupToolbarChip, PopupActionButton, PopupExportButtons } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import { OptimizationPipelineChartFrame, OptimizationPipelineChartEmptyState, OptimizationPipelineChartKeySelector, OptimizationPipelineChartModeToggle, OptimizationPipelineChartTooltip, getChartPortalTooltipProps } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineChart';

import EdtDynamicCheckService from 'app/admin/edtDynamicCheck/service/EdtDynamicCheckService';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getIndicatorColorClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const DEBUG = false;
const WS_PING_MS = 15000;
const THROTTLE_MS = 300;
const MAX_POINTS = 10000;

const STATUS_ITEM_STYLE = { flex: '1 1 170px' };
const VARIANCE_BELOW_SIGMA_CHIP_STYLE = { backgroundColor: 'rgba(196,238,208)' };

const roundConfig = {
    base: { label: 'Base', bg: 'rgba(245,158,11,.14)', chipBg: 'rgba(245,158,11,.18)', chipColor: '#92400e' },
    best: { label: 'Best', bg: 'rgba(34,197,94,.14)', chipBg: 'rgba(34,197,94,.18)', chipColor: '#166534' }
};

const datasetOptions = [
    { value: 'values', label: 'Values' },
    { value: 'variances', label: 'Variances' },
    { value: 'alarmVariables', label: 'Alarm variables' }
];

const rightViewOptions = [
    { value: 'chart', label: 'Chart' },
    { value: 'debug', label: 'Debug' }
];

function log(...args) { if (DEBUG) console.log('[DynamicValidationPopup]', ...args); }
function isPlainObject(v) { return v && typeof v === 'object' && !Array.isArray(v); }
function sanitizeJsonString(raw) { return typeof raw === 'string' ? raw.replace(/\bNaN\b/g, 'null').replace(/\bInfinity\b/g, 'null').replace(/\b-Infinity\b/g, 'null') : raw; }
function parseEpoch(v) { if (v == null) return NaN; if (typeof v === 'number') return v; const t = Date.parse(String(v)); return Number.isFinite(t) ? t : NaN; }
function formatTs(t) { const d = new Date(Number(t)); return Number.isFinite(d.getTime()) ? format(d, 'yyyy.MM.dd. HH:mm:ss', { locale: hu }) : ''; }
function stringifyDebugValue(value) { try { return JSON.stringify(value, null, 2); } catch (e) { return String(value || ''); } }
function formatExportValue(value) { return value === null || value === undefined ? '' : value; }

function shouldCompleteStream(item) {
    return item?.round === 'best' && (item?.converged || item?.raw?.state === 'CONVERGED');
}

function getIterationCount(value, firstValue) {
    const n = Number(value);
    const first = Number(firstValue);

    if (!Number.isFinite(n)) return null;
    if (!Number.isFinite(first)) return n + 1;

    return n - first + 1;
}

function formatElapsed(ms) {
    if (!Number.isFinite(ms) || ms < 0) return '-';
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return h + 'h ' + m + 'm ' + s + 's';
    if (m > 0) return m + 'm ' + s + 's';
    return s + 's';
}

function normalizeNumericObject(obj) {
    if (!isPlainObject(obj)) return {};
    const out = {};

    Object.entries(obj).forEach(([k, v]) => {
        if (v == null) {
            out[k] = null;
            return;
        }

        const n = Number(v);
        out[k] = Number.isFinite(n) ? n : null;
    });

    return out;
}

function getPayloadIteration(payload) {
    const direct = Number(payload?.iteration);
    if (Number.isFinite(direct)) return direct;
    const match = String(payload?.message || '').match(/iteration:\s*(\d+)/i);
    if (!match) return undefined;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function buildOverallIndexTicks(rows) {
    const iterations = rows.map(row => Number(row?.overallIndex)).filter(Number.isFinite);
    if (!iterations.length) return [];

    const min = Math.min(...iterations);
    const max = Math.max(...iterations);
    const count = iterations.length;
    const step = count > 100 ? 5 : count > 50 ? 2 : 1;
    const ticks = [];

    for (let i = min; i <= max; i += step) ticks.push(i);
    if (!ticks.includes(max)) ticks.push(max);

    return ticks;
}

function StatusItem(props) {
    const { label, value, icon: Icon, color = 'gray', className = '', style = {} } = props;

    return (
        <div className={'h-36 min-w-0 flex items-center gap-8 rounded pl-10 pr-16 ' + getIndicatorColorClass(color, 'bg-gray-100 text-gray-900') + (className ? ' ' + className : '')} style={{ ...STATUS_ITEM_STYLE, ...style }}>
            {Icon && <Icon className='flex-shrink-0 text-14' />}
            <div className='w-full min-w-0 flex items-center justify-between gap-5 leading-none'>
                <span className='text-xs whitespace-nowrap'>{label}</span>
                <span className='text-xs font-semibold capitalize truncate'>{value}</span>
            </div>
        </div>
    );
}

export default function DynamicValidationPopup() {
    const { popup, closePopup, optimizationPipelineData } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    const chartRef = useRef(null);

    const payload = popup?.payload || {};
    const simId = popup?.edtDynamicCheck || popup?.simulationId || optimizationPipelineData?.edtDynamicCheck || null;
    const basePayload = payload.base || null;
    const bestPayload = payload.best || null;

    const protocol = process.env.REACT_APP_WS_SECURE === 'true' ? 'wss' : 'ws';
    const host = process.env.REACT_APP_WEBSOCKET || window.location.host;
    const port = process.env.REACT_APP_WS_PORT ? ':' + process.env.REACT_APP_WS_PORT : '';
    const path = process.env.REACT_APP_EDT_WS_PATH || '';
    const wsUrl = protocol + '://' + host + port + path;

    const wsRef = useRef(null);
    const pingRef = useRef(null);
    const reconnectRef = useRef({ shouldReconnect: true, attempt: 0 });
    const mountedRef = useRef(false);
    const hasStartedRef = useRef(false);
    const handleStartRef = useRef(null);
    const streamCompletedRef = useRef(false);
    const bufferRef = useRef([]);
    const varianceBelowSigmaRef = useRef(new Set());
    const iterationRef = useRef({ overallCount: 0, base: null, best: null, firstBase: null, firstBest: null });
    const keysRef = useRef({ values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() });
    const visibleRef = useRef({ values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() });

    const [wsState, setWsState] = useState('idle');
    const [running, setRunning] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [rawEvents, setRawEvents] = useState([]);
    const [allKeys, setAllKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [hoveredKey, setHoveredKey] = useState(null);
    const [datasetMode, setDatasetMode] = useState('values');
    const [lineType, setLineType] = useState('step');
    const [rightTab, setRightTab] = useState('chart');
    const [latestStatus, setLatestStatus] = useState(null);
    const [iterationStatus, setIterationStatus] = useState({ overall: '-', base: '-', best: '-' });
    const [error, setError] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [showSigmaWithVariance, setShowSigmaWithVariance] = useState(true);
    const [varianceBelowSigmaKeys, setVarianceBelowSigmaKeys] = useState([]);

    const datasetModeRef = useRef(datasetMode);

    useEffect(() => {
        datasetModeRef.current = datasetMode;
    }, [datasetMode]);

    const colorForKey = (key) => {
        let h = 0;
        for (let i = 0; i < key.length; i++) h = ((h * 31) + key.charCodeAt(i)) % 360;
        return 'hsl(' + h + ',70%,40%)';
    };

    const strokeForKey = (key) => selectedKeys.length === 1 && selectedKeys[0] === key ? '#252f3e' : colorForKey(key);
    const getVarianceChipStyle = useCallback((key) => {
        if (datasetMode !== 'variances') return null;

        return varianceBelowSigmaKeys.includes(key) ? VARIANCE_BELOW_SIGMA_CHIP_STYLE : null;
    }, [datasetMode, varianceBelowSigmaKeys]);

    const isSeriesHighlighted = useCallback((key) => {
        if (!hoveredKey) return true;
        return hoveredKey === key;
    }, [hoveredKey]);

    const getSeriesOpacity = useCallback((key) => (
        isSeriesHighlighted(key) ? 1 : 0.18
    ), [isSeriesHighlighted]);

    const getSeriesStrokeWidth = useCallback((key) => {
        if (!hoveredKey) return 2;
        return isSeriesHighlighted(key) ? 2 : 1;
    }, [hoveredKey, isSeriesHighlighted]);

    const stopPing = useCallback(() => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    }, []);

    const send = useCallback((msg) => {
        const ws = wsRef.current;
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    }, []);

    const completeStream = useCallback(() => {
        streamCompletedRef.current = true;
        hasStartedRef.current = false;
        setRunning(false);
        stopPing();
        send({ command: 'resign' });

        try { wsRef.current?.close(); } catch (e) { }
    }, [send, stopPing]);

    const signup = useCallback(() => {
        if (!simId) { setError('Missing edtDynamicCheck id'); return false; }
        if (wsRef.current?.readyState !== WebSocket.OPEN) { setError('WebSocket not open'); return false; }

        streamCompletedRef.current = false;
        send({ command: 'signup', id: simId });
        stopPing();
        pingRef.current = setInterval(() => { send({ command: 'ping' }); }, WS_PING_MS);

        return true;
    }, [simId, send, stopPing]);

    const resign = useCallback(() => { stopPing(); send({ command: 'resign' }); }, [send, stopPing]);

    const doStart = useCallback(async () => {
        if (hasStartedRef.current) return true;

        if (!simId || !basePayload || !bestPayload) {
            setError('Missing edtDynamicCheck or base / best payload');
            return false;
        }

        try {
            hasStartedRef.current = true;
            streamCompletedRef.current = false;
            await EdtDynamicCheckService.start(simId, basePayload, bestPayload);
            setRunning(true);
            return true;
        } catch (e) {
            hasStartedRef.current = false;
            setError('start() failed: ' + String(e?.message || e));
            return false;
        }
    }, [simId, basePayload, bestPayload]);

    const handleTerminate = useCallback(async () => {
        try { if (simId) await EdtDynamicCheckService.terminate(simId); }
        catch (e) { setError('terminate() failed: ' + String(e?.message || e)); }

        streamCompletedRef.current = true;
        hasStartedRef.current = false;
        setRunning(false);
        resign();
    }, [simId, resign]);

    const handleStart = useCallback(async () => {
        setError(null);
        streamCompletedRef.current = false;
        const signed = signup();
        if (!signed) return;
        await doStart();
    }, [signup, doStart]);

    useEffect(() => { handleStartRef.current = handleStart; }, [handleStart]);

    const handleClose = useCallback(async () => {
        if (running && simId) {
            try { await EdtDynamicCheckService.terminate(simId); } catch (e) { }
        }

        streamCompletedRef.current = true;
        hasStartedRef.current = false;
        setRunning(false);
        resign();
        closePopup();
    }, [running, simId, resign, closePopup]);

    const handleCopy = useCallback(async (key, value) => {
        const text = typeof value === 'string' ? value : stringifyDebugValue(value);

        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 1200);
        } catch (e) {
            setError('copy failed: ' + String(e?.message || e));
        }
    }, []);

    const buildChartRows = useCallback((mode) => {
        const activeKeys = [...visibleRef.current[mode]];

        return bufferRef.current.map(r => {
            const src = r[mode] || {};
            const row = { overallIndex: r.overallIndex, overallCount: r.overallCount, ts: r.ts, iteration: r.iteration, baseIteration: r.baseIteration, bestIteration: r.bestIteration, round: r.round, alarm: r.alarm, converged: r.converged, initialized: r.initialized };

            activeKeys.forEach(k => {
                row[k] = src[k];
                if (mode === 'variances') row[k + '__sigma'] = r.sigmas?.[k] ?? null;
            });

            return row;
        });
    }, []);

    const syncKeys = useCallback((dataset, newKeys = []) => {
        const keys = [...keysRef.current[dataset]].sort();
        const vis = new Set(visibleRef.current[dataset]);

        if (!vis.size) keys.forEach(k => vis.add(k));
        newKeys.forEach(k => vis.add(k));
        visibleRef.current[dataset] = vis;

        if (dataset === datasetModeRef.current) {
            setAllKeys(keys);
            setSelectedKeys(keys.filter(k => vis.has(k)));
        }
    }, []);

    const normalizeWsEvent = useCallback((msg) => {
        if (!msg || typeof msg !== 'object') return null;
        if (msg.command === 'pong') return { kind: 'heartbeat' };

        const payload = isPlainObject(msg.payload) ? msg.payload : {};
        const payloadIteration = getPayloadIteration(payload);

        if (msg.type === 'iterating-values') {
            const t = parseEpoch(payload.time);

            return {
                kind: 'point',
                ts: Number.isFinite(t) ? t : Date.now(),
                iteration: payloadIteration,
                round: payload.round?.toLowerCase?.(),
                initialized: !!payload.finished,
                alarm: !!payload.alarm,
                converged: !!payload.converged || msg.state === 'CONVERGED',
                values: normalizeNumericObject(payload.variables_to_monitor),
                variances: normalizeNumericObject(payload.variances),
                sigmas: normalizeNumericObject(payload.sigmas),
                alarmVariables: normalizeNumericObject(payload.alarm_variables),
                raw: msg
            };
        }

        return { kind: 'status', ts: Date.now(), iteration: payloadIteration, message: payload.message || msg.type, round: payload.round?.toLowerCase?.(), converged: msg.state === 'CONVERGED', raw: msg };
    }, []);

    const updateIterationStatus = useCallback((item) => {
        const next = { ...iterationRef.current };
        const n = Number(item?.iteration);

        if (item?.round === 'base' && Number.isFinite(n)) {
            if (next.firstBase == null) next.firstBase = n;
            next.base = getIterationCount(n, next.firstBase);
        }

        if (item?.round === 'best' && Number.isFinite(n)) {
            if (next.firstBest == null) next.firstBest = n;
            next.best = getIterationCount(n, next.firstBest);
        }

        next.overallCount = (Number(next.base) || 0) + (Number(next.best) || 0);
        iterationRef.current = next;

        setIterationStatus({
            overall: next.overallCount || '-',
            base: next.base ?? '-',
            best: next.best ?? '-'
        });

        return next;
    }, []);

    const appendMessage = useCallback((raw) => {
        let parsed;

        try { parsed = JSON.parse(sanitizeJsonString(raw)); }
        catch (e) { return; }

        const item = normalizeWsEvent(parsed);
        if (!item || item.kind === 'heartbeat') return;
        if (streamCompletedRef.current) return;

        setRawEvents(prev => [...prev.slice(-199), { id: Date.now() + Math.random(), ts: Date.now(), data: parsed }]);

        if (item.kind === 'status') {
            updateIterationStatus(item);

            setLatestStatus(prev => ({
                ...(prev || {}),
                ...item,
                iteration: item.iteration ?? prev?.iteration,
                round: item.round ?? prev?.round,
                alarm: prev?.alarm,
                initialized: prev?.initialized,
                converged: item.converged || prev?.converged
            }));

            if (shouldCompleteStream(item)) completeStream();
            return;
        }

        if (item.kind === 'point') {
            const overallIndex = bufferRef.current.length;
            const iterations = updateIterationStatus(item);

            ['values', 'variances', 'sigmas', 'alarmVariables'].forEach(dataset => {
                const src = item[dataset] || {};
                const inserted = [];

                Object.keys(src).forEach(k => {
                    if (!keysRef.current[dataset].has(k)) {
                        keysRef.current[dataset].add(k);
                        inserted.push(k);
                    }
                });

                if (inserted.length) syncKeys(dataset, inserted);
            });

            bufferRef.current.push({ ...item, overallIndex, overallCount: iterations.overallCount, baseIteration: iterations.base, bestIteration: iterations.best });
            if (bufferRef.current.length > MAX_POINTS) bufferRef.current = bufferRef.current.slice(-MAX_POINTS);

            const nextVarianceBelowSigma = new Set(varianceBelowSigmaRef.current);
            Object.entries(item.variances || {}).forEach(([key, varianceValue]) => {
                const variance = Number(varianceValue);
                const sigma = Number(item.sigmas?.[key]);

                if (Number.isFinite(variance) && Number.isFinite(sigma) && variance < sigma) nextVarianceBelowSigma.add(key);
                else nextVarianceBelowSigma.delete(key);
            });
            varianceBelowSigmaRef.current = nextVarianceBelowSigma;
            setVarianceBelowSigmaKeys([...nextVarianceBelowSigma]);

            setLatestStatus({ ...item, overallIndex, overallCount: iterations.overallCount, baseIteration: iterations.base, bestIteration: iterations.best });
            if (shouldCompleteStream(item)) completeStream();
        }
    }, [normalizeWsEvent, syncKeys, completeStream, updateIterationStatus]);

    useEffect(() => {
        if (popup?.type !== 'DYNAMIC_VALIDATION') return undefined;

        mountedRef.current = true;
        reconnectRef.current.shouldReconnect = true;
        streamCompletedRef.current = false;
        bufferRef.current = [];
        varianceBelowSigmaRef.current = new Set();
        iterationRef.current = { overallCount: 0, base: null, best: null, firstBase: null, firstBest: null };
        keysRef.current = { values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() };
        visibleRef.current = { values: new Set(), variances: new Set(), sigmas: new Set(), alarmVariables: new Set() };
        setChartData([]);
        setRawEvents([]);
        setAllKeys([]);
        setSelectedKeys([]);
        setLatestStatus(null);
        setIterationStatus({ overall: '-', base: '-', best: '-' });
        setHoveredKey(null);
        setVarianceBelowSigmaKeys([]);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        setWsState('connecting');

        ws.onopen = () => { if (!mountedRef.current) return; setWsState('open'); handleStartRef.current?.(); };
        ws.onmessage = (e) => { if (!mountedRef.current) return; appendMessage(e.data); };
        ws.onerror = () => { if (!mountedRef.current) return; setWsState('error'); };
        ws.onclose = () => { if (!mountedRef.current) return; setWsState('closed'); stopPing(); };

        return () => {
            mountedRef.current = false;
            stopPing();
            try { ws.close(); } catch (e) { }
            wsRef.current = null;
        };
    }, [popup?.type, wsUrl, appendMessage, stopPing]);

    useEffect(() => {
        const t = setInterval(() => {
            setChartData(buildChartRows(datasetModeRef.current));
        }, THROTTLE_MS);

        return () => clearInterval(t);
    }, [buildChartRows]);

    useEffect(() => {
        const keys = [...keysRef.current[datasetMode]].sort();
        const vis = visibleRef.current[datasetMode];

        if (!vis.size) {
            keys.forEach(k => vis.add(k));
        }

        setAllKeys(keys);
        setSelectedKeys(keys.filter(k => vis.has(k)));
        setHoveredKey(null);
        setChartData(buildChartRows(datasetMode));
    }, [datasetMode, buildChartRows]);

    const roundBands = useMemo(() => {
        const rows = chartData.filter(r => r?.round && r?.overallIndex != null);
        if (!rows.length) return [];

        const xValues = rows.map(row => Number(row.overallIndex)).filter(Number.isFinite);
        if (!xValues.length) return [];

        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        const bands = [];
        let start = 0;

        for (let i = 1; i <= rows.length; i++) {
            const prev = rows[i - 1];
            const curr = rows[i];

            if (!curr || curr.round !== prev.round) {
                bands.push({
                    x1: Math.max(minX, Number(rows[start].overallIndex) - 0.5),
                    x2: Math.min(maxX, Number(rows[i - 1].overallIndex) + 0.5),
                    round: rows[start].round
                });

                start = i;
            }
        }

        return bands;
    }, [chartData]);

    const roundElapsed = useMemo(() => {
        const result = { base: '-', best: '-' };
        const map = {};

        chartData.forEach(row => {
            if (!row?.round || !Number.isFinite(Number(row.ts))) return;
            if (!map[row.round]) map[row.round] = { start: Number(row.ts), end: Number(row.ts) };
            map[row.round].start = Math.min(map[row.round].start, Number(row.ts));
            map[row.round].end = Math.max(map[row.round].end, Number(row.ts));
        });

        Object.keys(result).forEach(round => { if (map[round]) result[round] = formatElapsed(map[round].end - map[round].start); });

        return result;
    }, [chartData]);

    const overallIndexTicks = useMemo(() => buildOverallIndexTicks(chartData), [chartData]);

    const socketStatus = {
        open: { label: 'Connected', color: 'green' },
        connecting: { label: 'Connecting', color: 'yellow' },
        error: { label: 'Error', color: 'red' },
        closed: { label: latestStatus?.round === 'best' && latestStatus?.converged ? 'Completed' : 'Disconnected', color: latestStatus?.round === 'best' && latestStatus?.converged ? 'green' : 'gray' },
        idle: { label: 'Idle', color: 'gray' }
    }[wsState] || { label: '-', color: 'gray' };

    const debugJson = useMemo(() => stringifyDebugValue(rawEvents.map(item => ({ ts: formatTs(item.ts), data: item.data }))), [rawEvents]);

    const chartExportConfig = useMemo(() => {
        const columns = [
            { key: 'overallIndex', label: 'Overall index', sortable: true },
            { key: 'overallCount', label: 'Iteration count', sortable: true },
            { key: 'ts', label: 'Time', sortable: true, exportValue: row => formatTs(row.ts) },
            { key: 'round', label: 'Round', sortable: true },
            { key: 'iteration', label: 'Round iteration', sortable: true },
            { key: 'baseIteration', label: 'Base iteration count', sortable: true },
            { key: 'bestIteration', label: 'Best iteration count', sortable: true },
            { key: 'alarm', label: 'Alarm', sortable: true },
            { key: 'initialized', label: 'Initialized', sortable: true },
            { key: 'converged', label: 'Converged', sortable: true }
        ];

        selectedKeys.forEach((key) => {
            columns.push({
                key,
                label: key,
                sortable: true,
                exportValue: row => formatExportValue(row[key])
            });

            if (datasetMode === 'variances' && showSigmaWithVariance) {
                columns.push({
                    key: key + '__sigma',
                    label: key + ' sigma',
                    sortable: true,
                    exportValue: row => formatExportValue(row[key + '__sigma'])
                });
            }
        });

        return { columns, sort: { key: 'overallIndex', direction: 'asc' } };
    }, [datasetMode, selectedKeys, showSigmaWithVariance]);

    const debugExportConfig = useMemo(() => ({
        columns: [
            { key: 'ts', label: 'Time', sortable: true, exportValue: row => formatTs(row.ts) },
            { key: 'data', label: 'Data', exportValue: row => stringifyDebugValue(row.data) }
        ],
        sort: { key: 'ts', direction: 'asc' }
    }), []);

    const exportDisabled = rightTab === 'debug' ? !rawEvents.length : !chartData.length || !selectedKeys.length;

    function handleExport(formatType) {
        if (rightTab === 'debug') {
            exportPopupTable({
                tableConfig: debugExportConfig,
                rows: rawEvents,
                filename: buildExportFilename('dynamic-validation-debug', latestStatus?.ts || Date.now(), 'yy-MM-dd-HH-mm'),
                format: formatType
            });
            return;
        }

        exportPopupTable({
            tableConfig: chartExportConfig,
            rows: chartData,
            filename: buildExportFilename('dynamic-validation-' + datasetMode, latestStatus?.ts || Date.now(), 'yy-MM-dd-HH-mm'),
            format: formatType
        });
    }

    const TooltipContent = ({ active, payload }) => {
        if (!active || !payload?.length) return null;

        const p = payload[0].payload;

        return (
            <OptimizationPipelineChartTooltip title={'Iteration count ' + (p.overallCount ?? '-')} minWidth={220} maxWidth={340}>
                <div>{formatTs(p.ts)}</div>
                <div>Round: {p.round || '-'}</div>
                <div>Round iteration: {p.iteration ?? '-'}</div>
                <div>Base iteration count: {p.baseIteration ?? '-'}</div>
                <div>Best iteration count: {p.bestIteration ?? '-'}</div>
                <div>Overall index: {p.overallIndex ?? '-'}</div>
                <div>Alarm: {String(!!p.alarm)}</div>
                <div>Initialized: {String(!!p.initialized)}</div>
                <div>Converged: {String(!!p.converged)}</div>
                {payload.map(e => <div key={e.dataKey}>{e.name}: {e.value}</div>)}
            </OptimizationPipelineChartTooltip>
        );
    };

    const tooltipProps = getChartPortalTooltipProps(TooltipContent, chartRef);

    return (
        <ThemeProvider theme={mainTheme}>
            <OptimizationPipelinePopup open={popup?.type === 'DYNAMIC_VALIDATION'} onClose={handleClose} width='1500px' minWidth={700} maxWidth={1500} overflowVisible>
                <OptimizationPipelinePopupHeader title='EDT Dynamic Check' subtitle='Live dynamic validation' icon={PiPulseDuotone} onClose={handleClose}>
                    <PopupExportButtons disabled={exportDisabled} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
                    <PopupActionButton label={running ? 'Terminate' : 'Start'} icon={running ? FcCancel : FcStart} className='bg-white' onClick={running ? handleTerminate : handleStart} />
                </OptimizationPipelinePopupHeader>

                <PopupToolbar className='flex-col items-stretch px-0'>
                    <div className='w-full'>
                        <OptimizationPipelineChartKeySelector className='w-full' options={allKeys} value={selectedKeys} label='Search attributes' placeholder='Search and select attributes' getColor={(key) => strokeForKey(key)} getChipStyle={getVarianceChipStyle} onChange={(v) => { const set = new Set(v || []); visibleRef.current[datasetMode] = set; setSelectedKeys(v || []); setChartData(buildChartRows(datasetMode)); }} onHoverKeyChange={setHoveredKey} />
                    </div>

                    <div className='w-full flex flex-wrap items-center justify-between gap-10'>
                        <div className='flex flex-wrap items-center gap-8'>
                            <PopupToolbarGroup label='Dataset:'>
                                <OptimizationPipelineChartModeToggle value={datasetMode} onChange={setDatasetMode} options={datasetOptions} />
                            </PopupToolbarGroup>

                            {datasetMode === 'variances' && (
                                <PopupToolbarGroup label='Sigma:'>
                                    <PopupToolbarChip label={showSigmaWithVariance ? 'Hide' : 'Show'} active={showSigmaWithVariance} onClick={() => setShowSigmaWithVariance(prev => !prev)} />
                                </PopupToolbarGroup>
                            )}
                        </div>

                        <div className='flex flex-wrap items-center justify-end gap-8'>
                            <PopupToolbarGroup label='Line:'>
                                <PopupToolbarChip label={lineType === 'step' ? 'Interpolated' : 'Step'} onClick={() => setLineType(p => p === 'step' ? 'monotone' : 'step')} />
                            </PopupToolbarGroup>

                            <PopupToolbarGroup label='View:'>
                                <OptimizationPipelineChartModeToggle value={rightTab} onChange={setRightTab} options={rightViewOptions} />
                            </PopupToolbarGroup>
                        </div>
                    </div>

                    <div className='w-full flex flex-wrap gap-8'>
                        <StatusItem icon={PiPlugDuotone} label='Status' value={socketStatus.label} color={socketStatus.color} />
                        <StatusItem icon={PiArrowsClockwiseDuotone} label='Iteration count' value={iterationStatus.overall} />
                        <StatusItem icon={PiArrowsClockwiseDuotone} label='Base iteration' value={iterationStatus.base} color='yellow' />
                        <StatusItem icon={PiArrowsClockwiseDuotone} label='Best iteration' value={iterationStatus.best} color='green' />
                        <StatusItem icon={PiFlagDuotone} label='Round' value={roundConfig[latestStatus?.round]?.label || latestStatus?.round || '-'} color={latestStatus?.round === 'best' ? 'green' : latestStatus?.round === 'base' ? 'yellow' : 'gray'} />
                        <StatusItem icon={PiWarningDuotone} label='Alarm' value={String(!!latestStatus?.alarm)} color={latestStatus?.alarm ? 'red' : 'green'} />
                        <StatusItem icon={PiCheckCircleDuotone} label='Initialized' value={String(!!latestStatus?.initialized)} color={latestStatus?.initialized ? 'green' : 'gray'} />
                        <StatusItem icon={PiTargetDuotone} label='Converged' value={String(!!latestStatus?.converged)} color={latestStatus?.converged ? 'green' : 'gray'} />
                        <StatusItem icon={PiTimerDuotone} label='Base time' value={roundElapsed.base} color='yellow' />
                        <StatusItem icon={PiTimerDuotone} label='Best time' value={roundElapsed.best} color='green' />
                    </div>

                    {error && <div className='text-xs text-red-600'>{error}</div>}
                </PopupToolbar>

                <PopupBody padding={false} scroll>
                    {rightTab === 'chart' ? (
                        <div className='w-full p-20'>
                            {!chartData.length ? (
                                <OptimizationPipelineChartEmptyState>Waiting for data...</OptimizationPipelineChartEmptyState>
                            ) : (
                                <OptimizationPipelineChartFrame ref={chartRef} height={480} overflowVisible>
                                    <LineChart data={chartData} margin={{ top: 10, left: 10, right: 10, bottom: 10 }}>
                                        {roundBands.map((band, i) => <ReferenceArea key={i} x1={band.x1} x2={band.x2} fill={roundConfig[band.round]?.bg} />)}
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='overallIndex' ticks={overallIndexTicks} type='number' allowDecimals={false} domain={['dataMin', 'dataMax']} interval={0} />
                                        <YAxis />
                                        <Tooltip {...tooltipProps} />

                                        {selectedKeys.map(key => (
                                            <Line key={key} dataKey={key} name={key} type={lineType} stroke={strokeForKey(key)} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} dot={false} connectNulls isAnimationActive={false} />
                                        ))}

                                        {datasetMode === 'variances' && showSigmaWithVariance && selectedKeys.map(key => (
                                            <Line key={key + '__sigma'} dataKey={key + '__sigma'} name={key + ' sigma'} type={lineType} stroke={strokeForKey(key)} strokeWidth={getSeriesStrokeWidth(key)} strokeOpacity={getSeriesOpacity(key)} strokeDasharray='5 5' dot={false} connectNulls isAnimationActive={false} />
                                        ))}
                                    </LineChart>
                                </OptimizationPipelineChartFrame>
                            )}
                        </div>
                    ) : (
                        <div className='w-full min-h-full flex flex-col bg-white'>
                            <div className='flex-shrink-0 px-20 py-12 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-12'>
                                <div className='min-w-0 flex items-center gap-8'>
                                    <PiBracketsCurlyDuotone className='text-18 text-gray-700' />
                                    <div className='min-w-0'>
                                        <div className='text-13 font-semibold text-gray-900'>Raw event log</div>
                                        <div className='text-11 text-gray-500'>{rawEvents.length} events stored</div>
                                    </div>
                                </div>

                                <PopupToolbarChip label={copiedKey === 'all' ? 'Copied' : 'Copy all'} onClick={() => handleCopy('all', debugJson)} />
                            </div>

                            <div className='flex-1 min-h-0 overflow-auto'>
                                {!rawEvents.length ? (
                                    <div className='p-20'>
                                        <OptimizationPipelineChartEmptyState>Waiting for debug events...</OptimizationPipelineChartEmptyState>
                                    </div>
                                ) : (
                                    <div className='w-full flex flex-col'>
                                        {rawEvents.slice().reverse().map((item) => {
                                            const copyKey = 'event_' + item.id;

                                            return (
                                                <div key={item.id} className='border-b border-gray-200 bg-white'>
                                                    <div className='flex items-center justify-between gap-10 px-20 py-10 bg-gray-50 border-b border-gray-100'>
                                                        <div className='text-xs text-gray-500'>{formatTs(item.ts)}</div>

                                                        <button type='button' className='flex items-center gap-4 text-11 text-gray-600 hover:text-black transition-colors' onClick={() => handleCopy(copyKey, item.data)}>
                                                            {copiedKey === copyKey ? <TbCheck className='text-14' /> : <TbCopy className='text-14' />}
                                                            {copiedKey === copyKey ? 'Copied' : 'Copy'}
                                                        </button>
                                                    </div>

                                                    <pre className='m-0 px-20 py-12 text-11 leading-relaxed whitespace-pre-wrap break-all text-gray-800'>{stringifyDebugValue(item.data)}</pre>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </PopupBody>
            </OptimizationPipelinePopup>
        </ThemeProvider>
    );
}
